import { useMemo } from "react";
import { QueryClient, queryOptions, useQuery, type Query } from "@tanstack/react-query";
import { PEOPLE_CACHED } from "../../shared/social";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { BucketList, Peak } from "../../shared/schemas";
import { parseRouteLink, type RoutePreview } from "../../shared/routeLink";
import { parseGpx } from "../../shared/gpx";
import { api, apiFetch, fileUrl } from "./api";
import { getBlob, kv } from "./idb";
import { applyToAscents, applyToExtraPeaks, applyToLists, applyToPins, applyToProfile, withBucketList, type ClientAscent, type ClientProfile } from "./ops";
import { unsyncedOps } from "./outbox";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 1000 * 60 * 60 * 24 * 30,
      // Serve cached data offline; only hit the network when it's there.
      networkMode: "offlineFirst",
      retry: (count, err) => count < 1 && !(err as Error)?.message?.includes("session"),
      refetchOnWindowFocus: false, // sync.ts refetches on visibility after flushing the outbox
    },
    mutations: { networkMode: "always" },
  },
});

/** Persist the query cache to IndexedDB so lists, passport and ascents open with no signal. */
export const persister = createAsyncStoragePersister({
  storage: {
    getItem: (k) => kv.get<string>(k).then((v) => v ?? null),
    setItem: (k, v) => kv.set(k, v).catch(() => undefined), // quota → keep running from memory
    removeItem: (k) => kv.del(k),
  },
  key: "rq-cache",
  throttleTime: 1500,
});
export const PERSIST_MAX_AGE = 1000 * 60 * 60 * 24 * 60;
export const CACHE_BUSTER = "v1";

/**
 * What goes to IndexedDB: successful queries, minus searches, and of other people only the
 * PEOPLE_CACHED most recently viewed (their card, passport, stats, ascents and comparison).
 */
export function shouldPersist(query: Query): boolean {
  if (query.state.status !== "success") return false;
  const [head, id] = query.queryKey as unknown[];
  if (head === "people") return false;
  if (head !== "person" || typeof id !== "string") return true;
  const recent = new Set(
    queryClient
      .getQueryCache()
      .findAll({ queryKey: ["person"] })
      .filter((q) => q.queryKey.length === 2)
      .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
      .slice(0, PEOPLE_CACHED)
      .flatMap((q) => {
        const card = q.state.data as { id?: string } | null | undefined;
        return [q.queryKey[1] as string, ...(card?.id ? [card.id] : [])];
      }),
  );
  return recent.has(id);
}

export const keys = {
  ascents: ["ascents"] as const,
  lists: ["lists"] as const,
  extraPeaks: ["peaks", "extra"] as const,
  pins: ["pins"] as const,
  profile: ["profile"] as const,
  peak: (id: string) => ["peak", id] as const,
  config: ["config"] as const,
  // Social (lib/social.ts). People's pages are routed by handle, so their card is cached by handle too.
  socialMe: ["social", "me"] as const,
  // Group ascents: the invitations waiting for me, and my ascents' companions and invitations sent.
  invites: ["social", "invites"] as const,
  groups: ["social", "groups"] as const,
  person: (handle: string) => ["person", `@${handle.toLowerCase()}`] as const,
  personPart: (userId: string, part: "passport" | "stats" | "ascents") => ["person", userId, part] as const,
  personAscent: (userId: string, ascentId: string) => ["person", userId, "ascent", ascentId] as const,
  following: ["follows"] as const,
  followers: ["follows", "followers"] as const,
  requests: ["follows", "requests"] as const,
  blocks: ["follows", "blocks"] as const,
  notices: ["notices"] as const,
  noticesCount: ["notices", "count"] as const,
  push: ["push"] as const,
  feed: ["feed"] as const,
  comments: (ownerId: string, ascentId: string) => ["comments", ownerId, ascentId] as const,
  // With the insignias since October 2026 (the version keeps an older device's cached list apart).
  challenges: (month: string) => ["challenges", month, 2] as const,
};

/**
 * GET /api/config: what the server switches on (social, boards, feed) and the key devices subscribe to
 * avisos with (null while push is off), cached so it is known offline too.
 */
export type AppConfig = { turnstileSiteKey: string; registration: string; passkeys: boolean; social?: boolean; boards?: boolean; feed?: boolean; pushKey?: string | null };

export function useConfig() {
  return useQuery({ queryKey: keys.config, queryFn: () => api.get<AppConfig>("/api/config"), staleTime: 10 * 60_000 });
}

/** The social features are on (the server's SOCIAL switch). Unknown (first launch offline) counts as off. */
export const useSocialOn = () => !!useConfig().data?.social;

/**
 * Server data + local unsynced ops. A refetch while the outbox still holds changes must never
 * make those changes disappear from the UI.
 */
export async function withOverlay<T>(data: T, apply: (d: T, op: Awaited<ReturnType<typeof unsyncedOps>>[number]) => T): Promise<T> {
  const ops = await unsyncedOps();
  return ops.reduce(apply, data);
}

export function useAscents() {
  return useQuery({
    queryKey: keys.ascents,
    queryFn: async () => withOverlay(await api.get<ClientAscent[]>("/api/ascents"), applyToAscents),
  });
}

/** In the user's order, the Bucket list first (an empty one until the server has it). */
const orderLists = (lists: BucketList[]) => withBucketList([...lists].sort((a, b) => a.position - b.position));

export function useLists() {
  return useQuery({
    queryKey: keys.lists,
    queryFn: async () => withOverlay(await api.get<BucketList[]>("/api/lists"), applyToLists),
    select: orderLists,
  });
}

/** Peak ids pinned to the profile, in order (may include peaks no longer climbed; callers filter). */
export function usePins() {
  return useQuery({
    queryKey: keys.pins,
    queryFn: async () => withOverlay(await api.get<string[]>("/api/pins"), applyToPins),
  });
}

/** The signed-in user's profile (avatar), with a picture still in the outbox shown from IndexedDB. */
export function useProfile() {
  return useQuery({
    queryKey: keys.profile,
    queryFn: async () => withOverlay<ClientProfile>(await api.get<ClientProfile>("/api/profile"), applyToProfile),
    staleTime: 5 * 60_000,
  });
}

/**
 * What a Wikiloc/Strava link says about the route. Wikiloc is read from the URL alone; Strava
 * activities are enriched by the server (cached for good, so the card still renders offline).
 */
export function useRoutePreview(url: string | null | undefined): RoutePreview | null {
  const local = useMemo(() => parseRouteLink(url), [url]);
  const { data } = useQuery({
    queryKey: ["route-link", local?.url],
    enabled: local?.provider === "strava",
    staleTime: Infinity,
    retry: false,
    queryFn: () => api.get<RoutePreview>(`/api/external/route-link?url=${encodeURIComponent(local!.url)}`),
  });
  return useMemo(() => data ?? (local ? { ...local, date: null, startTime: null, imageUrl: null } : null), [data, local]);
}

/** Custom + live-imported peaks (the static catalog covers the rest). */
export function useExtraPeaks() {
  return useQuery({
    queryKey: keys.extraPeaks,
    queryFn: async () => withOverlay(await api.get<Peak[]>("/api/peaks?extra=1"), applyToExtraPeaks),
    staleTime: 5 * 60_000,
  });
}

export type PeakDetail = {
  peak: Peak;
  ascents: { id: string; date: string; activity: string; routeName: string | null }[];
  lists: { id: string; name: string; kind: string }[];
};

export function usePeakDetail(id: string | undefined) {
  return useQuery({
    queryKey: keys.peak(id ?? ""),
    queryFn: () => api.get<PeakDetail>(`/api/peaks/${id}`),
    enabled: !!id,
    retry: false,
  });
}

/** An ascent's GPX track, parsed: the local copy while it is still unsynced, else the stored file. Kept for good. */
export function gpxQuery(a: Pick<ClientAscent, "gpxKey" | "localGpxKey">) {
  const { gpxKey, localGpxKey } = a;
  return queryOptions({
    queryKey: ["gpx", localGpxKey ?? gpxKey],
    enabled: !!(localGpxKey || gpxKey),
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60 * 24 * 30,
    queryFn: async () => {
      const text = localGpxKey
        ? await (await getBlob(localGpxKey))?.text()
        : await (await apiFetch(fileUrl(gpxKey!), { headers: { Accept: "application/gpx+xml" } })).text();
      return text ? parseGpx(text) : null;
    },
  });
}
